import type {
  CcCommandMatrixMembersResponse,
  CcCommandMatrixParams,
  CcCommandMatrixProjectsResponse,
  CcEventsByNameParams,
  CcEventsNameBreakdownResponse,
  CcEventsProjectBreakdownResponse,
  CcEventsQueryParams,
  CcEventsSummaryResponse,
  CcEventsTimeseriesParams,
  CcEventsTimeseriesResponse,
  CcEventsUserBreakdownResponse,
  CcUsageMembersResponse,
  CcUsageQueryParams,
  CcUsageRepositoryBreakdownResponse,
  CcUsageSummaryResponse,
  CcUsageTeamsResponse,
  CcUsageTimeseriesByGroupParams,
  CcUsageTimeseriesByGroupResponse,
  CcUsageTimeseriesParams,
  CcUsageTimeseriesResponse,
  CcUsageUserBreakdownResponse,
  EngineerEvaluationDetailParams,
  EngineerEvaluationDetailResponse,
  EngineerEvaluationsParams,
  EngineerEvaluationsResponse,
  EngineerEvaluationSummary,
  McpUsageQueryParams,
  McpUsageServerBreakdownResponse,
  McpUsageSummaryResponse,
  McpUsageTeamsResponse,
  McpUsageTimeseriesParams,
  McpUsageTimeseriesResponse,
  McpUsageToolBreakdownResponse,
  McpUsageUserBreakdownResponse,
} from '../../types/api.js';
import type {
  CombatPowerHistory,
  CombatPowerResponse,
  CombatPowerCustomViewInput,
  CombatPowerCustomViewsResponse,
  DailyWorkRatiosResponse,
  RequirementPendingResourceListResponse,
} from '../../types/combat-power.js';
import { request } from './_request.js';

/** @graph-connects none */
export const engineerEvaluationApi = {
  getEvaluations: (params?: EngineerEvaluationsParams): Promise<EngineerEvaluationsResponse> => {
    const q = new URLSearchParams();
    if (params?.quarter) q.set('quarter', params.quarter);
    if (params?.groupName) q.set('groupName', params.groupName);
    const qs = q.toString();
    return request<EngineerEvaluationsResponse>(`/engineer-evaluations${qs ? `?${qs}` : ''}`);
  },
  getDetail: (
    params: EngineerEvaluationDetailParams,
  ): Promise<EngineerEvaluationDetailResponse> => {
    const q = new URLSearchParams({
      email: params.email,
      quarter: params.quarter,
    });
    return request<EngineerEvaluationDetailResponse>(
      `/engineer-evaluations/detail?${q.toString()}`,
    );
  },
  getMySummary: (): Promise<{ summaries: EngineerEvaluationSummary[] }> => {
    return request<{ summaries: EngineerEvaluationSummary[] }>('/engineer-evaluations/my-summary');
  },
};

/**
 * cc-usage query string を作る。
 * @graph-connects none
 */
function buildCcUsageQuery(
  params?: CcUsageQueryParams | CcUsageTimeseriesParams | CcUsageTimeseriesByGroupParams,
): string {
  const query = new URLSearchParams();
  if (params?.scope) query.set('scope', params.scope);
  if (params?.team) query.set('team', params.team);
  if (params?.email) query.set('email', params.email);
  if (params?.range) query.set('range', params.range);
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (params?.excludeTeams && params.excludeTeams.length > 0) {
    query.set('excludeTeams', params.excludeTeams.join(','));
  }
  if (params?.excludeNicknames && params.excludeNicknames.length > 0) {
    query.set('excludeNicknames', params.excludeNicknames.join(','));
  }
  if (params && 'granularity' in params && typeof params.granularity === 'string') {
    query.set('granularity', params.granularity);
  }
  if (params && 'groupBy' in params && typeof params.groupBy === 'string') {
    query.set('groupBy', params.groupBy);
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Claude Code 利用量 dashboard APIクライアント。/cc-usage/* エンドポイントを呼び出し
 * @graph-connects /cc-usage/summary [calls] cc-usage サマリAPI
 * @graph-connects /cc-usage/timeseries [calls] cc-usage 時系列API
 * @graph-connects /cc-usage/timeseries-by-group [calls] cc-usage 積み上げ時系列API
 * @graph-connects /cc-usage/by-user [calls] cc-usage user breakdown API
 * @graph-connects /cc-usage/by-repository [calls] cc-usage repo breakdown API
 * @graph-connects /cc-usage/by-team [calls] cc-usage team breakdown API
 * @graph-connects /cc-usage/teams [calls] cc-usage teams API
 * @graph-connects /cc-usage/members [calls] cc-usage members API (集計対象フィルタ選択肢)
 */
export const ccUsageApi = {
  getSummary: (params?: CcUsageQueryParams): Promise<CcUsageSummaryResponse> => {
    return request<CcUsageSummaryResponse>(`/cc-usage/summary${buildCcUsageQuery(params)}`);
  },
  getTimeseries: (params?: CcUsageTimeseriesParams): Promise<CcUsageTimeseriesResponse> => {
    return request<CcUsageTimeseriesResponse>(`/cc-usage/timeseries${buildCcUsageQuery(params)}`);
  },
  getTimeseriesByGroup: (
    params: CcUsageTimeseriesByGroupParams,
  ): Promise<CcUsageTimeseriesByGroupResponse> => {
    return request<CcUsageTimeseriesByGroupResponse>(
      `/cc-usage/timeseries-by-group${buildCcUsageQuery(params)}`,
    );
  },
  getByUser: (params?: CcUsageQueryParams): Promise<CcUsageUserBreakdownResponse> => {
    return request<CcUsageUserBreakdownResponse>(`/cc-usage/by-user${buildCcUsageQuery(params)}`);
  },
  getByRepository: (params?: CcUsageQueryParams): Promise<CcUsageRepositoryBreakdownResponse> => {
    return request<CcUsageRepositoryBreakdownResponse>(
      `/cc-usage/by-repository${buildCcUsageQuery(params)}`,
    );
  },
  getByTeam: (params?: CcUsageQueryParams): Promise<CcUsageTeamsResponse> => {
    return request<CcUsageTeamsResponse>(`/cc-usage/by-team${buildCcUsageQuery(params)}`);
  },
  getTeams: (
    params?: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>,
  ): Promise<CcUsageTeamsResponse> => {
    return request<CcUsageTeamsResponse>(`/cc-usage/teams${buildCcUsageQuery(params)}`);
  },
  getMembers: (
    params?: Pick<CcUsageQueryParams, 'range' | 'from' | 'to'>,
  ): Promise<CcUsageMembersResponse> => {
    return request<CcUsageMembersResponse>(`/cc-usage/members${buildCcUsageQuery(params)}`);
  },
};

/**
 * cc-events query string を作る。cc-usage と同じ規約。
 * event_type は backend のクエリパラメータ名 (snake_case) に合わせて変換する。
 * @graph-connects none
 */
function buildCcEventsQuery(
  params?:
    | CcEventsQueryParams
    | CcEventsByNameParams
    | CcEventsTimeseriesParams
    | CcCommandMatrixParams,
): string {
  const query = new URLSearchParams();
  if (params?.range) query.set('range', params.range);
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  // 集計対象トグルは true のときだけ明示 (既定 false は送らず backend の除外既定に任せる)。
  if (params?.includeCortex) query.set('includeCortex', 'true');
  if (params?.includeSlackTriage) query.set('includeSlackTriage', 'true');
  if (params && 'eventType' in params && typeof params.eventType === 'string') {
    query.set('event_type', params.eventType);
  }
  if (params && 'granularity' in params && typeof params.granularity === 'string') {
    query.set('granularity', params.granularity);
  }
  if (params && 'team' in params && typeof params.team === 'string' && params.team.length > 0) {
    query.set('team', params.team);
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Claude Code 実行イベント dashboard APIクライアント。/cc-events/* エンドポイントを呼び出し
 * @graph-connects /cc-events/summary [calls] cc-events サマリAPI
 * @graph-connects /cc-events/by-name [calls] cc-events event_name 別ランキングAPI
 * @graph-connects /cc-events/by-user [calls] cc-events user breakdown API
 * @graph-connects /cc-events/by-project [calls] cc-events Backlog チケット別 breakdown API
 * @graph-connects /cc-events/timeseries [calls] cc-events 時系列API
 * @graph-connects /cc-events/command-matrix/members [calls] 規定コマンド利用状況 (人 × 工程) API
 * @graph-connects /cc-events/command-matrix/projects [calls] 規定コマンド利用状況 (PJ × 工程) API
 */
export const ccEventsApi = {
  getCommandMatrixMembers: (
    params?: CcCommandMatrixParams,
  ): Promise<CcCommandMatrixMembersResponse> => {
    return request<CcCommandMatrixMembersResponse>(
      `/cc-events/command-matrix/members${buildCcEventsQuery(params)}`,
    );
  },
  getCommandMatrixProjects: (
    params?: CcCommandMatrixParams,
  ): Promise<CcCommandMatrixProjectsResponse> => {
    return request<CcCommandMatrixProjectsResponse>(
      `/cc-events/command-matrix/projects${buildCcEventsQuery(params)}`,
    );
  },
  getSummary: (params?: CcEventsQueryParams): Promise<CcEventsSummaryResponse> => {
    return request<CcEventsSummaryResponse>(`/cc-events/summary${buildCcEventsQuery(params)}`);
  },
  getByName: (params?: CcEventsByNameParams): Promise<CcEventsNameBreakdownResponse> => {
    return request<CcEventsNameBreakdownResponse>(
      `/cc-events/by-name${buildCcEventsQuery(params)}`,
    );
  },
  getByUser: (params?: CcEventsQueryParams): Promise<CcEventsUserBreakdownResponse> => {
    return request<CcEventsUserBreakdownResponse>(
      `/cc-events/by-user${buildCcEventsQuery(params)}`,
    );
  },
  getByProject: (params?: CcEventsQueryParams): Promise<CcEventsProjectBreakdownResponse> => {
    return request<CcEventsProjectBreakdownResponse>(
      `/cc-events/by-project${buildCcEventsQuery(params)}`,
    );
  },
  getTimeseries: (params?: CcEventsTimeseriesParams): Promise<CcEventsTimeseriesResponse> => {
    return request<CcEventsTimeseriesResponse>(
      `/cc-events/timeseries${buildCcEventsQuery(params)}`,
    );
  },
};

/**
 * mcp-usage query string を作る。cc-usage と同じ規約。
 * @graph-connects none
 */
function buildMcpUsageQuery(params?: McpUsageQueryParams | McpUsageTimeseriesParams): string {
  const query = new URLSearchParams();
  if (params?.scope) query.set('scope', params.scope);
  if (params?.team) query.set('team', params.team);
  if (params?.email) query.set('email', params.email);
  if (params?.range) query.set('range', params.range);
  if (params?.from) query.set('from', params.from);
  if (params?.to) query.set('to', params.to);
  if (params && 'granularity' in params && typeof params.granularity === 'string') {
    query.set('granularity', params.granularity);
  }
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 全社 MCP 利用量 dashboard APIクライアント。/mcp-usage/* エンドポイントを呼び出し
 * @graph-connects /mcp-usage/summary [calls] mcp-usage サマリAPI
 * @graph-connects /mcp-usage/timeseries [calls] mcp-usage 時系列API
 * @graph-connects /mcp-usage/by-server [calls] mcp-usage server breakdown API
 * @graph-connects /mcp-usage/by-tool [calls] mcp-usage tool breakdown API
 * @graph-connects /mcp-usage/by-user [calls] mcp-usage user breakdown API
 * @graph-connects /mcp-usage/teams [calls] mcp-usage teams API
 */
export const mcpUsageApi = {
  getSummary: (params?: McpUsageQueryParams): Promise<McpUsageSummaryResponse> => {
    return request<McpUsageSummaryResponse>(`/mcp-usage/summary${buildMcpUsageQuery(params)}`);
  },
  getTimeseries: (params?: McpUsageTimeseriesParams): Promise<McpUsageTimeseriesResponse> => {
    return request<McpUsageTimeseriesResponse>(
      `/mcp-usage/timeseries${buildMcpUsageQuery(params)}`,
    );
  },
  getByServer: (params?: McpUsageQueryParams): Promise<McpUsageServerBreakdownResponse> => {
    return request<McpUsageServerBreakdownResponse>(
      `/mcp-usage/by-server${buildMcpUsageQuery(params)}`,
    );
  },
  getByTool: (params?: McpUsageQueryParams): Promise<McpUsageToolBreakdownResponse> => {
    return request<McpUsageToolBreakdownResponse>(
      `/mcp-usage/by-tool${buildMcpUsageQuery(params)}`,
    );
  },
  getByUser: (params?: McpUsageQueryParams): Promise<McpUsageUserBreakdownResponse> => {
    return request<McpUsageUserBreakdownResponse>(
      `/mcp-usage/by-user${buildMcpUsageQuery(params)}`,
    );
  },
  getTeams: (
    params?: Pick<McpUsageQueryParams, 'range' | 'from' | 'to'>,
  ): Promise<McpUsageTeamsResponse> => {
    return request<McpUsageTeamsResponse>(`/mcp-usage/teams${buildMcpUsageQuery(params)}`);
  },
};

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 戦闘力サマリー APIクライアント。/combat-power/* (データ・履歴・カスタムビュー) を呼び出し
 * @graph-connects /combat-power [calls] 戦闘力データ取得 API
 * @graph-connects /combat-power/history [calls] 戦闘力履歴取得 API
 * @graph-connects /combat-power/custom-views [calls] 戦闘力カスタムビュー取得・保存 API
 * @graph-connects /combat-power/daily-work-ratios [calls] メンバー日次稼働割合取得 API
 * @graph-connects /combat-power/requirement-pending-resources [calls] 待処理要件定義pt 取得 API
 */
export const combatPowerApi = {
  getData: (sheet?: string): Promise<CombatPowerResponse> => {
    const q = sheet ? `?sheet=${encodeURIComponent(sheet)}` : '';
    return request<CombatPowerResponse>(`/combat-power${q}`);
  },
  getHistory: (name: string): Promise<CombatPowerHistory> => {
    return request<CombatPowerHistory>(`/combat-power/history?name=${encodeURIComponent(name)}`);
  },
  getDailyWorkRatios: (start: string, end: string): Promise<DailyWorkRatiosResponse> => {
    const q = `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
    return request<DailyWorkRatiosResponse>(`/combat-power/daily-work-ratios${q}`);
  },
  getRequirementPendingResources: (): Promise<RequirementPendingResourceListResponse> => {
    return request<RequirementPendingResourceListResponse>(
      '/combat-power/requirement-pending-resources',
    );
  },
  getCustomViews: (): Promise<CombatPowerCustomViewsResponse> => {
    return request<CombatPowerCustomViewsResponse>('/combat-power/custom-views');
  },
  saveCustomViews: (
    views: CombatPowerCustomViewInput[],
  ): Promise<CombatPowerCustomViewsResponse> => {
    return request<CombatPowerCustomViewsResponse>('/combat-power/custom-views', {
      method: 'PUT',
      body: JSON.stringify({ views }),
    });
  },
};
